/**
 * How far a scan has got, from the engine's events, for the sidebar.
 *
 * A scan is two steps: listing the folder (tags and headers, about a
 * millisecond a song -- `library.scanning` {seen, total}) and then reading
 * each new song through for its fingerprint, several at once in the
 * background (`library.identifying` {done, total}). Analysis follows.
 */

import { useEffect, useState } from "react";
import { bus } from "./bus";

export interface ScanProgress {
  /** Listing the folder: files looked at so far, of all found. */
  listing: { seen: number; total: number } | null;
  /** Reading new songs through: done so far, of all listed. */
  reading: { done: number; total: number } | null;
}

let current: ScanProgress = { listing: null, reading: null };
const listeners = new Set<(p: ScanProgress) => void>();

function set(next: ScanProgress) {
  current = next;
  listeners.forEach((fn) => fn(current));
}

let started = false;
function start() {
  if (started) return;
  started = true;
  bus.on<{ seen?: number; total?: number; finished?: boolean }>("library.scanning", (e) => {
    const { seen, total, finished } = e.data ?? {};
    if (finished) set({ ...current, listing: null });
    else if (typeof seen === "number" && typeof total === "number") {
      set({ ...current, listing: seen >= total ? null : { seen, total } });
    } else set({ ...current, listing: { seen: 0, total: 0 } }); // started, still walking
  });
  bus.on<{ done?: number; total?: number; finished?: boolean }>("library.identifying", (e) => {
    const { done, total, finished } = e.data ?? {};
    if (finished || typeof done !== "number" || typeof total !== "number" || done >= total) {
      set({ ...current, reading: null });
    } else {
      set({ ...current, reading: { done, total } });
    }
  });
}

export function useScanProgress(): ScanProgress {
  start();
  const [p, setP] = useState(current);
  useEffect(() => {
    listeners.add(setP);
    setP(current);
    return () => {
      listeners.delete(setP);
    };
  }, []);
  return p;
}
